import { useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import {
  FaCircleCheck,
  FaCreditCard,
  FaLock,
  FaRotateRight,
  FaTriangleExclamation,
  FaWallet,
} from 'react-icons/fa6'
import {
  CARD_BRANDS,
  getPaymentProviderDetails,
  getPaymentRemovalImpact,
  getPaymentSetupSteps,
  getPaymentVerificationState,
  normalizeCardLast4,
  validatePaymentMethodInput,
} from './paymentUtils'

export default function PaymentsPanel({ account, onAddPaymentMethod, onCreatePaymentSetup, onCompletePaymentSetup, onRemovePaymentMethod, onSetPrimaryPaymentMethod, paymentProvider }) {
  const [brand, setBrand] = useState('Visa')
  const [last4, setLast4] = useState('')
  const [primary, setPrimary] = useState(true)
  const [localError, setLocalError] = useState('')
  const [stripeSetup, setStripeSetup] = useState(null)
  const [stripeLoading, setStripeLoading] = useState(false)
  const [savingManual, setSavingManual] = useState(false)
  const [primaryBusyId, setPrimaryBusyId] = useState('')
  const [removeBusyId, setRemoveBusyId] = useState('')
  const { bidderStatusLabel, methodCountLabel, methods, primaryMethod, setupComplete, verified } = getPaymentVerificationState(account)
  const stripeMode = paymentProvider === 'stripe'
  const providerDetails = getPaymentProviderDetails(paymentProvider)
  const setupSteps = getPaymentSetupSteps(account)
  const stripePromise = useMemo(() => (
    stripeSetup?.publishableKey ? loadStripe(stripeSetup.publishableKey) : null
  ), [stripeSetup?.publishableKey])

  const handleSubmit = async (event) => {
    event.preventDefault()
    const result = validatePaymentMethodInput({ brand, last4 }, methods)

    if (!result.ok) {
      setLocalError(result.error)
      return
    }

    setLocalError('')
    setSavingManual(true)

    try {
      await onAddPaymentMethod?.({ ...result.payload, primary: primary || !methods.length })
      setLast4('')
      setPrimary(true)
    } catch (error) {
      setLocalError(error.message || 'Could not save payment method.')
    } finally {
      setSavingManual(false)
    }
  }

  const handleStartStripeSetup = async () => {
    setLocalError('')
    setStripeLoading(true)

    try {
      const setup = await onCreatePaymentSetup?.()
      setStripeSetup(setup)
    } catch (error) {
      setLocalError(error.message || 'Could not start secure card setup.')
    } finally {
      setStripeLoading(false)
    }
  }

  const handleStripeComplete = async (setupIntentId) => {
    await onCompletePaymentSetup?.({ setupIntentId, primary: true })
    setStripeSetup(null)
  }

  const handleSetPrimary = async (methodId) => {
    if (!methodId) return

    setPrimaryBusyId(methodId)
    setLocalError('')

    try {
      await onSetPrimaryPaymentMethod?.(methodId)
    } catch (error) {
      setLocalError(error.message || 'Could not set primary card.')
    } finally {
      setPrimaryBusyId('')
    }
  }

  const handleRemove = async (methodId) => {
    if (!methodId) return

    setRemoveBusyId(methodId)
    setLocalError('')

    try {
      await onRemovePaymentMethod?.(methodId)
    } catch (error) {
      setLocalError(error.message || 'Could not remove payment method.')
    } finally {
      setRemoveBusyId('')
    }
  }

  return (
    <section className="obc-payments">
      <div className="obc-payments__hero">
        <div>
          <span className="obc-payment-form__eyebrow"><FaLock /> Payments & bidder verification</span>
          <h3>{verified ? 'Your bidder card is ready' : 'Set up your bidder card'}</h3>
          <p>
            {verified
              ? `${primaryMethod.brand} ending ${primaryMethod.last4} is active for auction bids, quick checkout, and weekly bundle payments.`
              : 'Add one primary card to unlock verified bidding, faster checkout, and cleaner order handling.'}
          </p>
        </div>
        <div className={`obc-payments__hero-status ${verified ? 'is-success' : 'is-danger'}`}>
          {verified ? <FaCircleCheck /> : <FaTriangleExclamation />}
          <strong>{bidderStatusLabel}</strong>
          <span>{methodCountLabel}</span>
        </div>
      </div>

      <div className="obc-payments__steps" aria-label="Payment setup progress">
        {setupSteps.map((step, index) => (
          <article className={step.complete ? 'is-complete' : undefined} key={step.id}>
            <b>{step.complete ? <FaCircleCheck /> : index + 1}</b>
            <div>
              <strong>{step.title}</strong>
              <span>{step.detail}</span>
            </div>
          </article>
        ))}
      </div>

      <div className="obc-payments__status">
        <article className={verified ? 'is-success' : 'is-danger'}>
          {verified ? <FaCircleCheck /> : <FaTriangleExclamation />}
          <strong>{verified ? 'Verified bidder active' : 'Bidder verification required'}</strong>
          <span>{verified ? `${primaryMethod.brand} ending ${primaryMethod.last4} is the primary bidding card.` : 'A saved primary card is required before auction bids and quick checkout.'}</span>
        </article>
        <article>
          <FaCreditCard />
          <strong>{methodCountLabel}</strong>
          <span>{primaryMethod ? `${primaryMethod.brand} ending ${primaryMethod.last4} is primary.` : 'No cards are saved on this account yet.'}</span>
        </article>
        <article>
          <FaWallet />
          <strong>{providerDetails.mode === 'stripe' ? 'Stripe enabled' : 'Local test mode'}</strong>
          <span>{providerDetails.securityNote}</span>
        </article>
      </div>

      <div className="obc-payments__layout">
        <div className="obc-payment-form">
          <div>
            <span className="obc-payment-form__eyebrow">Verification card</span>
            <h4>{providerDetails.title}</h4>
            <p>{providerDetails.description}</p>
          </div>
          {stripeMode ? (
            <button className="obc-dashboard__bid-button" type="button" disabled={stripeLoading} onClick={handleStartStripeSetup}>
              {stripeLoading ? 'Starting Stripe setup...' : providerDetails.action}
            </button>
          ) : null}
          {stripeSetup && stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: stripeSetup.clientSecret,
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#83a551',
                    colorBackground: '#11190d',
                    colorText: '#f6ffe9',
                    colorDanger: '#ff8f76',
                    borderRadius: '12px',
                    fontFamily: 'Poppins, system-ui, sans-serif',
                  },
                },
              }}
            >
              <StripeSetupForm onComplete={handleStripeComplete} />
            </Elements>
          ) : null}
          {!stripeMode ? <form className="obc-payment-form__manual" onSubmit={handleSubmit}>
            <div className="obc-payment-form__test-card">
              <FaCreditCard />
              <div>
                <strong>Recommended local test card</strong>
                <span>Visa ending 4242</span>
              </div>
            </div>
            <label>
              Card brand
              <select value={brand} onChange={(event) => setBrand(event.target.value)}>
                {CARD_BRANDS.map((item) => (
                  <option value={item} key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              Last 4 digits
              <input
                inputMode="numeric"
                maxLength="4"
                pattern="\d{4}"
                placeholder="4242"
                value={last4}
                onChange={(event) => setLast4(normalizeCardLast4(event.target.value))}
              />
            </label>
            <label className="obc-payment-form__check">
              <input type="checkbox" checked={primary} onChange={(event) => setPrimary(event.target.checked)} />
              Set as primary bidding and checkout card
            </label>
            {localError ? <p className="obc-payment-form__error">{localError}</p> : null}
            <button className="obc-dashboard__bid-button" type="submit" disabled={savingManual}>
              {savingManual ? 'Saving card...' : providerDetails.action}
            </button>
          </form> : localError ? <p className="obc-payment-form__error">{localError}</p> : null}
        </div>

        <div className="obc-payment-methods">
          <div className="obc-dashboard__side-head">
            <h3>Saved Methods</h3>
            <span>{methodCountLabel}</span>
          </div>
          {methods.map((method) => {
            const removalImpact = getPaymentRemovalImpact(methods, method.id)
            const isPrimaryBusy = primaryBusyId === method.id
            const isRemoveBusy = removeBusyId === method.id

            return (
              <article className={method.primary ? 'is-primary' : undefined} key={method.id || `${method.brand}-${method.last4}`}>
                <FaCreditCard />
                <div>
                  <strong>{method.brand} ending {method.last4}</strong>
                  <span>{method.primary ? 'Primary bidder card' : 'Backup payment method'} / {method.provider === 'stripe' ? 'Stripe' : 'Internal dev'}</span>
                  <small className={removalImpact.code === 'last-card' ? 'is-danger' : undefined}>{removalImpact.message}</small>
                </div>
                <div className="obc-payment-methods__actions">
                  {method.primary ? (
                    <b>Primary</b>
                  ) : (
                    <button type="button" disabled={!method.id || isPrimaryBusy || Boolean(removeBusyId)} onClick={() => handleSetPrimary(method.id)}>
                      {isPrimaryBusy ? <FaRotateRight /> : null} Set Primary
                    </button>
                  )}
                  <button className="is-danger" type="button" disabled={!removalImpact.canRemove || isRemoveBusy || Boolean(primaryBusyId)} onClick={() => handleRemove(method.id)}>
                    {isRemoveBusy ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </article>
            )
          })}
          {!methods.length ? (
            <div className="obc-payment-methods__empty">
              <FaTriangleExclamation />
              <strong>No saved card</strong>
              <span>Save one verification card to unlock auction bidding and quick checkout.</span>
            </div>
          ) : null}
          {setupComplete ? (
            <div className="obc-payment-methods__note">
              <FaLock />
              <span>Only card brand, last four digits, provider, and primary status are displayed in the dashboard.</span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

function StripeSetupForm({ onComplete }) {
  const stripe = useStripe()
  const elements = useElements()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setSaving(true)
    setError('')

    const submitResult = await elements.submit()

    if (submitResult.error) {
      setError(submitResult.error.message || 'Review your card details.')
      setSaving(false)
      return
    }

    const { error: setupError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    })

    if (setupError) {
      setError(setupError.message || 'Stripe could not verify this card.')
      setSaving(false)
      return
    }

    if (setupIntent?.status !== 'succeeded') {
      setError('Card verification needs another step before it can be saved.')
      setSaving(false)
      return
    }

    try {
      await onComplete?.(setupIntent.id)
    } catch (completeError) {
      setError(completeError.message || 'Card verified, but the account could not be updated.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="obc-stripe-setup" onSubmit={handleSubmit}>
      <PaymentElement />
      {error ? <p className="obc-payment-form__error">{error}</p> : null}
      <button className="obc-dashboard__bid-button" type="submit" disabled={!stripe || saving}>
        {saving ? 'Verifying Card...' : 'Verify Card With Stripe'}
      </button>
    </form>
  )
}
